import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Mail, Phone, Edit, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { type Prospect } from '@/hooks/useProspects';

interface ProspectCardProps {
  prospect: Prospect;
  onEdit?: (prospect: Prospect) => void;
  onDelete?: (prospectId: string) => void;
}

export function ProspectCard({ prospect, onEdit, onDelete }: ProspectCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: prospect._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`cursor-move transition-all hover:shadow-md group ${
        isDragging ? 'opacity-50 rotate-2 shadow-lg' : ''
      }`}
      {...attributes}
      {...listeners}
    >
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Header with company name and actions */}
          <div className="flex items-start justify-between">
            <h3 className="font-medium text-sm line-clamp-2 flex-1 mr-2">
              {prospect.company}
            </h3>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <MoreHorizontal className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-32">
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit?.(prospect);
                  }}
                  className="text-xs"
                >
                  <Edit className="h-3 w-3 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.(prospect._id);
                  }}
                  className="text-xs text-red-600 focus:text-red-600"
                >
                  <Trash2 className="h-3 w-3 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Contact information */}
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground font-medium">
              {prospect.contactName}
            </p>
            
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Mail className="h-3 w-3 flex-shrink-0" />
              <span className="truncate" title={prospect.email}>
                {prospect.email}
              </span>
            </div>
            
            {prospect.phone && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Phone className="h-3 w-3 flex-shrink-0" />
                <span>{prospect.phone}</span>
              </div>
            )}
          </div>

          {/* Notes preview */}
          {prospect.notes && (
            <p className="text-xs text-muted-foreground line-clamp-2 bg-slate-50 p-2 rounded">
              {prospect.notes}
            </p>
          )}

          {/* Metadata */}
          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <span>
              {new Date(prospect.updatedAt).toLocaleDateString()}
            </span>
            <Badge variant="outline" className="text-xs py-0">
              ID: {prospect._id.slice(-4)}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}